/**
 * Cross-Selling-Champion (Jun-2026 · User-Wunsch).
 * Verkäufer mit der höchsten Zubehör- + Dienstleistungs-Provision im Monat.
 * Motiviert das Team spielerisch zum Mitverkauf (höchste Marge).
 * Erweiterung: Konfetti-Animation für den/die Champion(in) + Teilen-Button.
 */
import { useEffect, useRef, useState } from "react";
import { Trophy, Sparkles, Share2 } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";

const formatPrice = (n) => `€ ${Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

function fireConfetti() {
    const end = Date.now() + 1200;
    const colors = ["#FF8300", "#FFCC00", "#ffffff"];
    (function frame() {
        confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0 }, colors });
        confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1 }, colors });
        if (Date.now() < end) requestAnimationFrame(frame);
    })();
    confetti({ particleCount: 90, spread: 75, origin: { y: 0.4 }, colors });
}

export default function CrossSellingChampionCard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const firedRef = useRef(false);

    useEffect(() => {
        api.get("/dealer/cross-selling-champion")
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoaded(true));
    }, []);

    const c = data?.champion;
    const isWinner = !!(c && user && c.seller_id === user.id);

    const monthLabel = (() => {
        try { return new Date((data?.month || "") + "-01").toLocaleDateString("de-DE", { month: "long", year: "numeric" }); }
        catch { return data?.month; }
    })();

    // Konfetti einmal pro Monat für den Champion (sessionStorage-Schutz gegen Wiederholung).
    useEffect(() => {
        if (!isWinner || firedRef.current) return;
        const key = `tw_champ_confetti_${data.month}`;
        if (sessionStorage.getItem(key)) return;
        firedRef.current = true;
        sessionStorage.setItem(key, "1");
        const t = setTimeout(fireConfetti, 400);
        return () => clearTimeout(t);
    }, [isWinner, data]);

    if (!loaded || !data || !c) return null;

    const shareText = isWinner
        ? `🏆 Ich bin Cross-Selling-Champion im ${monthLabel} bei TwoWheels! ${formatPrice(c.total)} Zubehör- & Dienstleistungs-Provision. #TwoWheels`
        : `🏆 ${c.seller_name} ist Cross-Selling-Champion im ${monthLabel} bei TwoWheels — ${formatPrice(c.total)} Zubehör- & Dienstleistungs-Provision!`;

    const onShare = async () => {
        try {
            if (navigator.share) {
                await navigator.share({ title: "Cross-Selling-Champion", text: shareText });
            } else {
                await navigator.clipboard.writeText(shareText);
                toast.success("In die Zwischenablage kopiert — jetzt teilen! 🎉");
            }
        } catch {
            /* Abbruch durch Nutzer — kein Fehler nötig */
        }
    };

    return (
        <div className="relative overflow-hidden rounded-sm border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5" data-testid="cross-selling-champion-card">
            <div className="absolute -right-6 -top-6 opacity-10"><Trophy className="w-28 h-28 text-amber-500" /></div>
            <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider bg-amber-500 text-white px-2 py-0.5 rounded-full">
                        <Sparkles className="w-3 h-3" /> Cross-Selling-Champion
                    </span>
                    <span className="text-[11px] text-gray-500">{monthLabel}</span>
                    {isWinner && (
                        <span className="text-[10px] font-black uppercase tracking-wider bg-[#FF8300] text-white px-2 py-0.5 rounded-full" data-testid="champion-you-badge">Das bist du! 🎉</span>
                    )}
                    <button
                        type="button"
                        onClick={onShare}
                        className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-bold text-amber-700 hover:text-amber-900 border border-amber-300 hover:border-amber-500 rounded-sm px-2.5 py-1 transition-colors"
                        data-testid="champion-share-btn"
                        title="Erfolg teilen"
                    >
                        <Share2 className="w-3.5 h-3.5" /> Teilen
                    </button>
                </div>
                <div className="flex items-end justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-amber-500 shrink-0" />
                            <span className="text-lg font-extrabold text-[#111827] truncate" data-testid="champion-name">{c.seller_name}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Höchste Zubehör- & Dienstleistungs-Provision diesen Monat</p>
                    </div>
                    <div className="text-right shrink-0">
                        <div className="font-mono-num font-black text-2xl text-amber-600" data-testid="champion-total">{formatPrice(c.total)}</div>
                        <div className="text-[11px] text-gray-500">Zubehör {formatPrice(c.accessory)} · Service {formatPrice(c.service)}</div>
                    </div>
                </div>
                {data.ranking?.length > 1 && (
                    <div className="mt-4 pt-3 border-t border-amber-100 space-y-1.5" data-testid="champion-ranking">
                        {data.ranking.slice(1, 3).map((r, i) => (
                            <div key={r.seller_id} className="flex items-center justify-between text-xs">
                                <span className="text-gray-600">{i + 2}. {r.seller_name}</span>
                                <span className="font-mono-num font-bold text-gray-700">{formatPrice(r.total)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
